import { useCallback, useEffect, useState } from "react";
import {
  Check,
  Image,
  Loader2,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type { Contact, EmailMessage } from "@/lib/types";
import { formatRelativeDate } from "@/lib/utils";
import { EmailComposer } from "./EmailComposer";

const STATUSES: Contact["status"][] = [
  "discovered",
  "contacted",
  "prospect",
];

const STATUS_COLORS: Record<Contact["status"], string> = {
  discovered: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  prospect: "bg-green-100 text-green-700",
};

type MessageWithCounts = EmailMessage & { opens: number; clicks: number };

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  title: "",
  industry: "",
  phone: "",
  website: "",
  location: "",
  status: "discovered" as Contact["status"],
  notes: "",
};

function HubSpotIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="#ff7a59"
    >
      <path d="M17.63 13.77a3.15 3.15 0 0 0-1.81.57l-1.67-1.27a4.6 4.6 0 0 0 .5-2.08l1.52-.67a2.34 2.34 0 1 0-.56-1.3l-1.42.63a4.65 4.65 0 0 0-2.3-2.05V6.18a2.63 2.63 0 1 0-1.28 0v1.43a4.63 4.63 0 0 0-3.03 4.35 4.63 4.63 0 0 0 4.63 4.63 4.6 4.6 0 0 0 2.62-.82l1.72 1.31a3.15 3.15 0 1 0 2.08-3.31Zm-5.43 3.4a3.21 3.21 0 1 1 0-6.42 3.21 3.21 0 0 1 0 6.42Z" />
    </svg>
  );
}

type EditableFields = Omit<
  Contact,
  "id" | "created_by" | "created_at" | "updated_at" | "hubspot_contact_id"
>;

export function CrmTab() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("");
  const [selected, setSelected] = useState<Contact | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [messages, setMessages] = useState<MessageWithCounts[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<EditableFields>>({});
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (filter) params.set("status", filter);
    const res = await fetch(`/api/contacts?${params.toString()}`, {
      credentials: "include",
    });
    const data = await res.json();
    setContacts(data.contacts ?? []);
    setLoading(false);
  }, [search, filter]);

  useEffect(() => {
    load();
  }, [load]);

  async function loadMessages(contactId: string) {
    const res = await fetch(
      `/api/email/messages?contact_id=${encodeURIComponent(contactId)}`,
      { credentials: "include" }
    );
    const data = await res.json();
    setMessages(data.messages ?? []);
  }

  function startEditing() {
    if (!selected) return;
    setEditForm({
      first_name: selected.first_name ?? "",
      last_name: selected.last_name ?? "",
      email: selected.email ?? "",
      company: selected.company ?? "",
      title: selected.title ?? "",
      industry: selected.industry ?? "",
      phone: selected.phone ?? "",
      website: selected.website ?? "",
      location: selected.location ?? "",
      status: selected.status,
      source: selected.source ?? "",
      notes: selected.notes ?? "",
      cloudstudio_example_content_image_url:
        selected.cloudstudio_example_content_image_url ?? "",
    });
    setEditing(true);
  }

  async function saveEdit() {
    if (!selected) return;
    setEditSaving(true);
    const res = await fetch("/api/contacts", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: selected.id, ...editForm }),
    });
    if (res.ok) {
      const data = await res.json();
      const updated = data.contact as Contact;
      setContacts((cs) =>
        cs.map((c) => (c.id === updated.id ? updated : c))
      );
      setSelected(updated);
      setEditing(false);
    }
    setEditSaving(false);
  }

  async function deleteContact(id: string) {
    if (!confirm("Delete this contact?")) return;
    const res = await fetch(`/api/contacts?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      setContacts((cs) => cs.filter((c) => c.id !== id));
      if (selected?.id === id) {
        setSelected(null);
        setEditing(false);
      }
    }
  }

  async function createContact() {
    setAddSaving(true);
    const res = await fetch("/api/contacts", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...addForm,
        source: "manual",
      }),
    });
    if (res.ok) {
      const data = await res.json();
      setContacts((cs) => [data.contact, ...cs]);
      setAddForm(EMPTY_FORM);
      setAddOpen(false);
    }
    setAddSaving(false);
  }

  async function syncToHubspot(contact: Contact) {
    setSyncing(true);
    const res = await fetch("/api/hubspot/sync-contact", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact_id: contact.id }),
    });
    if (res.ok) {
      const data = await res.json();
      const updated: Contact = {
        ...contact,
        hubspot_contact_id: data.hubspot_contact_id,
        status: "prospect",
      };
      setContacts((cs) =>
        cs.map((c) => (c.id === contact.id ? updated : c))
      );
      setSelected(updated);
    }
    setSyncing(false);
  }

  const inputCls =
    "w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_400px]">
      <div className="rounded-xl border bg-card">
        <div className="flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold">Contacts</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-48 rounded-md border py-1.5 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-sm outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Contact
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left">
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Company
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Source
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-sm text-muted-foreground"
                  >
                    No contacts yet. Add one above or save prospects from the
                    Prospecting tab.
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => {
                      setSelected(c);
                      setEditing(false);
                      loadMessages(c.id);
                    }}
                    className={`cursor-pointer border-b last:border-b-0 hover:bg-slate-50/60 ${
                      selected?.id === c.id ? "bg-slate-50" : ""
                    }`}
                  >
                    <td className="px-6 py-3 text-sm">
                      <div className="flex items-center gap-2 font-medium">
                        {c.hubspot_contact_id && (
                          <HubSpotIcon className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span>
                          {[c.first_name, c.last_name]
                            .filter(Boolean)
                            .join(" ") || "\u2014"}
                        </span>
                      </div>
                      <div className={`text-xs text-muted-foreground ${c.hubspot_contact_id ? "ml-6" : ""}`}>
                        {c.email ?? ""}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      {c.company ?? "\u2014"}
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_COLORS[c.status] ?? "bg-slate-100"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm capitalize text-muted-foreground">
                      {c.source ?? "\u2014"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected ? (
        <div className="rounded-xl border bg-card">
          <div className="flex items-start justify-between border-b px-6 py-4">
            <div>
              <div className="flex items-center gap-2">
                {selected.hubspot_contact_id && (
                  <HubSpotIcon className="h-4 w-4" />
                )}
                <h3 className="text-base font-semibold">
                  {[selected.first_name, selected.last_name]
                    .filter(Boolean)
                    .join(" ") || "Contact"}
                </h3>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {selected.company ?? ""}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {!editing && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="rounded p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                  title="Edit contact"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setEditing(false);
                }}
                className="rounded p-1 text-muted-foreground hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-4 overflow-y-auto p-6" style={{ maxHeight: "calc(100vh - 280px)" }}>
            {editing ? (
              /* ---- Edit mode ---- */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      First name
                    </label>
                    <input
                      type="text"
                      value={editForm.first_name ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          first_name: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Last name
                    </label>
                    <input
                      type="text"
                      value={editForm.last_name ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          last_name: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Company
                    </label>
                    <input
                      type="text"
                      value={editForm.company ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          company: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          title: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={editForm.industry ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          industry: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={editForm.phone ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          phone: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Website
                    </label>
                    <input
                      type="text"
                      value={editForm.website ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          website: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Location
                    </label>
                    <input
                      type="text"
                      value={editForm.location ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          location: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Status
                  </label>
                  <select
                    value={editForm.status ?? "discovered"}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        status: e.target.value as Contact["status"],
                      }))
                    }
                    className="w-full rounded-md border bg-white px-3 py-1.5 text-sm capitalize outline-none focus:ring-2 focus:ring-ring"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Source
                  </label>
                  <input
                    type="text"
                    value={editForm.source ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        source: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={editForm.notes ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        notes: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    CloudStudio Example Content Image URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={
                      editForm.cloudstudio_example_content_image_url ?? ""
                    }
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        cloudstudio_example_content_image_url: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>

                <div className="flex gap-2 border-t pt-3">
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={editSaving}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {editSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                    {editSaving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="rounded-md border bg-white px-4 py-1.5 text-sm font-medium hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* ---- View mode ---- */
              <>
                <div className="space-y-2 text-sm">
                  {selected.email && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Email
                      </span>
                      <div>{selected.email}</div>
                    </div>
                  )}
                  {selected.title && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Title
                      </span>
                      <div>{selected.title}</div>
                    </div>
                  )}
                  {selected.industry && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Industry
                      </span>
                      <div>{selected.industry}</div>
                    </div>
                  )}
                  {selected.phone && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Phone
                      </span>
                      <div>{selected.phone}</div>
                    </div>
                  )}
                  {selected.website && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Website
                      </span>
                      <div>
                        <a
                          href={
                            selected.website.startsWith("http")
                              ? selected.website
                              : `https://${selected.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {selected.website}
                        </a>
                      </div>
                    </div>
                  )}
                  {selected.location && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Location
                      </span>
                      <div>{selected.location}</div>
                    </div>
                  )}
                  {selected.notes && (
                    <div>
                      <span className="text-xs text-muted-foreground">
                        Notes
                      </span>
                      <div className="text-xs">{selected.notes}</div>
                    </div>
                  )}
                  {selected.cloudstudio_example_content_image_url && (
                    <div>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Image className="h-3 w-3" />
                        CloudStudio Example Content
                      </span>
                      <a
                        href={selected.cloudstudio_example_content_image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block"
                      >
                        <img
                          src={selected.cloudstudio_example_content_image_url}
                          alt="CloudStudio example content"
                          className="max-h-40 rounded-md border object-cover"
                        />
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">
                    Status
                  </label>
                  <div className="mt-1">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_COLORS[selected.status] ?? "bg-slate-100"}`}>
                      {selected.status}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setComposerOpen(true)}
                    disabled={!selected.email}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={startEditing}
                    className="rounded-md border bg-white p-1.5 text-muted-foreground hover:bg-slate-50 hover:text-foreground"
                    title="Edit contact"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteContact(selected.id)}
                    className="rounded-md border bg-white p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {selected.hubspot_contact_id ? (
                  <a
                    href={`https://app.hubspot.com/contacts/${selected.hubspot_contact_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 rounded-md border border-[#ff7a59]/30 bg-[#ff7a59]/5 px-3 py-1.5 text-sm font-medium text-[#ff7a59] hover:bg-[#ff7a59]/10"
                  >
                    <HubSpotIcon className="h-3.5 w-3.5" />
                    View in HubSpot
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => syncToHubspot(selected)}
                    disabled={!selected.email || syncing}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-[#ff7a59]/30 bg-[#ff7a59]/5 px-3 py-1.5 text-sm font-medium text-[#ff7a59] hover:bg-[#ff7a59]/10 disabled:opacity-50"
                  >
                    {syncing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    {syncing ? "Syncing..." : "Sync to HubSpot"}
                  </button>
                )}

                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Email History
                  </h4>
                  {messages.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No emails sent yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {messages.map((m) => (
                        <div
                          key={m.id}
                          className="rounded-md border bg-slate-50 px-3 py-2 text-xs"
                        >
                          <div className="font-medium">{m.subject}</div>
                          <div className="mt-1 flex items-center justify-between text-muted-foreground">
                            <span>{formatRelativeDate(m.sent_at)}</span>
                            <span>
                              {m.opens} opens · {m.clicks} clicks
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {composerOpen && selected.email && (
            <EmailComposer
              contactId={selected.id}
              toEmail={selected.email}
              onClose={() => setComposerOpen(false)}
              onSent={() => {
                setComposerOpen(false);
                loadMessages(selected.id);
                if (selected.status === "discovered") {
                  const updated: Contact = { ...selected, status: "contacted" };
                  setSelected(updated);
                  setContacts((cs) =>
                    cs.map((c) => (c.id === selected.id ? updated : c))
                  );
                }
              }}
            />
          )}
        </div>
      ) : (
        <div className="hidden rounded-xl border border-dashed bg-card p-8 text-center text-sm text-muted-foreground lg:block">
          Select a contact to view details.
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-lg rounded-xl border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-base font-semibold">Add Contact</h3>
              <button
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  setAddForm(EMPTY_FORM);
                }}
                className="rounded p-1 text-muted-foreground hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createContact();
              }}
              className="space-y-3 p-6"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    First name
                  </label>
                  <input
                    type="text"
                    value={addForm.first_name}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        first_name: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Last name
                  </label>
                  <input
                    type="text"
                    value={addForm.last_name}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        last_name: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={addForm.email}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, email: e.target.value }))
                  }
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Company
                  </label>
                  <input
                    type="text"
                    value={addForm.company}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        company: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Title
                  </label>
                  <input
                    type="text"
                    value={addForm.title}
                    onChange={(e) =>
                      setAddForm((f) => ({ ...f, title: e.target.value }))
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Industry
                  </label>
                  <input
                    type="text"
                    value={addForm.industry}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        industry: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={addForm.phone}
                    onChange={(e) =>
                      setAddForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Website
                  </label>
                  <input
                    type="text"
                    value={addForm.website}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        website: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Location
                  </label>
                  <input
                    type="text"
                    value={addForm.location}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        location: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Status
                </label>
                <select
                  value={addForm.status}
                  onChange={(e) =>
                    setAddForm((f) => ({
                      ...f,
                      status: e.target.value as Contact["status"],
                    }))
                  }
                  className="w-full rounded-md border bg-white px-3 py-1.5 text-sm capitalize outline-none focus:ring-2 focus:ring-ring"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Notes
                </label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={2}
                  className={inputCls}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddOpen(false);
                    setAddForm(EMPTY_FORM);
                  }}
                  className="rounded-md border bg-white px-4 py-1.5 text-sm font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving || !addForm.email}
                  className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {addSaving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  {addSaving ? "Saving..." : "Add Contact"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
